import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ThumbsUp, ThumbsDown, MessageSquare, TrendingUp, TrendingDown, Users, Calendar, Eye, EyeOff } from 'lucide-react'
import { FeedbackService } from '@/services/feedbackService'
import { FeedbackAnalytics, FeedbackWithDetails } from '@shared/schemas/feedback'
import { format } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import 'highlight.js/styles/github.css'
import '@/styles/highlight-dark.css'

const FeedbackManagement: React.FC = () => {
  const [analytics, setAnalytics] = useState<FeedbackAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedFeedback, setExpandedFeedback] = useState<Set<string>>(new Set())
  
  useEffect(() => {
    loadFeedbackAnalytics()
  }, [])

  const loadFeedbackAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await FeedbackService.getAnalytics()
      setAnalytics(data)
    } catch (err) {
      console.error('Failed to load feedback analytics:', err)
      setError('Failed to load feedback data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const toggleFeedbackExpansion = (feedbackId: string) => {
    const newExpanded = new Set(expandedFeedback)
    if (newExpanded.has(feedbackId)) {
      newExpanded.delete(feedbackId)
    } else {
      newExpanded.add(feedbackId)
    }
    setExpandedFeedback(newExpanded)
  }

  const getRatingIcon = (rating: number) => {
    return rating === 1 ? (
      <ThumbsUp className="h-4 w-4 text-green-600" />
    ) : (
      <ThumbsDown className="h-4 w-4 text-red-600" />
    )
  }

  const getRatingBadge = (rating: number) => {
    return rating === 1 ? (
      <Badge variant="outline" className="text-green-700 border-green-300">
        <ThumbsUp className="h-3 w-3 mr-1" />
        Positive
      </Badge>
    ) : (
      <Badge variant="outline" className="text-red-700 border-red-300">
        <ThumbsDown className="h-3 w-3 mr-1" />
        Negative
      </Badge>
    )
  }

  const renderFeedbackCard = (feedback: FeedbackWithDetails) => {
    const isExpanded = expandedFeedback.has(feedback.id)
    
    return (
      <Card key={feedback.id} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {getRatingIcon(feedback.rating)}
              <div>
                <CardTitle className="text-sm font-medium">
                  {feedback.user.firstName} {feedback.user.lastName}
                </CardTitle>
                <CardDescription className="text-xs">
                  {feedback.message.session?.agentType} Agent • {format(new Date(feedback.createdAt), 'MMM d, yyyy h:mm a')}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getRatingBadge(feedback.rating)}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleFeedbackExpansion(feedback.id)}
              >
                {isExpanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0">
          {/* Message Preview - Only show when NOT expanded */}
          {!isExpanded && (
            <div className="mb-3 p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground mb-1">Agent Response:</p>
              <p className="text-sm line-clamp-2">{feedback.message.content}</p>
            </div>
          )}

          {/* Feedback Comment */}
          {feedback.comment && (
            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-muted-foreground mb-1">User Feedback:</p>
              <p className="text-sm">{feedback.comment}</p>
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && (
            <div className="space-y-3 pt-3 border-t border-border">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Message ID:</p>
                  <p className="font-mono text-xs">{feedback.messageId}</p>
                </div>
                {feedback.message.session && (
                  <>
                    <div>
                      <p className="text-muted-foreground">Session Title:</p>
                      <p>{feedback.message.session.title || 'Untitled Session'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Agent Type:</p>
                      <p>{feedback.message.session.agentType}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Full Message Content - No truncation with markdown rendering */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Full Agent Response:</p>
                <div className="p-3 bg-muted rounded-md">
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeHighlight]}
                      components={{
                        p: ({ children }) => <p className="mb-3 last:mb-0 text-sm leading-relaxed">{children}</p>,
                        ul: ({ children }) => <ul className="mb-3 last:mb-0 ml-4 space-y-1">{children}</ul>,
                        ol: ({ children }) => <ol className="mb-3 last:mb-0 ml-6 space-y-1 list-decimal list-outside">{children}</ol>,
                        li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
                        table: ({ children }) => (
                          <div className="overflow-x-auto my-4 rounded-lg border border-gray-300 dark:border-white shadow-sm bg-white dark:bg-black max-w-full">
                            <table className="w-full table-auto">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead className="bg-gray-100 dark:bg-gray-700 border-b-2 border-gray-300 dark:border-white">
                            {children}
                          </thead>
                        ),
                        tbody: ({ children }) => (
                          <tbody className="bg-white dark:bg-black">
                            {children}
                          </tbody>
                        ),
                        tr: ({ children }) => (
                          <tr className="border-b border-gray-200 dark:border-white/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                            {children}
                          </tr>
                        ),
                        th: ({ children }) => (
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wide border-r border-gray-300 dark:border-white last:border-r-0">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white border-r border-gray-300 dark:border-white last:border-r-0">
                            {children}
                          </td>
                        ),
                        code: ({ children, ...props }) => 
                          <code className="bg-background px-2 py-1 rounded text-sm font-mono" {...props}>
                            {children}
                          </code>,
                        pre: ({ children }) => 
                          <pre className="bg-background/50 p-4 rounded-md text-sm overflow-auto my-3 font-mono border max-w-full">
                            {children}
                          </pre>,
                        h1: ({ children }) => <h1 className="text-lg font-bold mb-3 mt-2">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-sm font-semibold mb-2 mt-2">{children}</h3>,
                        blockquote: ({ children }) => 
                          <blockquote className="border-l-4 border-primary pl-4 my-3 italic text-muted-foreground">
                            {children}
                          </blockquote>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        em: ({ children }) => <em className="italic">{children}</em>,
                        hr: () => <hr className="my-4 border-border" />
                      }}
                    >
                      {feedback.message.content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
        <div className="h-96 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={loadFeedbackAnalytics} variant="outline">
          Retry
        </Button>
      </div>
    )
  }

  if (!analytics) {
    return <div className="text-center py-8">No feedback data available</div>
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalFeedback}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">HR Agent</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.hrFeedback.total}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.hrFeedback.positivePercentage.toFixed(1)}% positive
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ops Agent</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.opsFeedback.total}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.opsFeedback.positivePercentage.toFixed(1)}% positive
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Rating</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(((analytics.hrFeedback.positive + analytics.opsFeedback.positive) / analytics.totalFeedback) * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">positive feedback</p>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Tabs */}
      <Tabs defaultValue="recent" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="recent">Recent Feedback</TabsTrigger>
          <TabsTrigger value="negative">Negative Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="recent" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Recent Feedback</h3>
            <Button onClick={loadFeedbackAnalytics} variant="outline" size="sm">
              Refresh
            </Button>
          </div>
          
          {analytics.recentFeedback.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-32">
                <p className="text-muted-foreground">No recent feedback available</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {analytics.recentFeedback.map(renderFeedbackCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="negative" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Negative Feedback</h3>
            <Badge variant="outline" className="text-red-700 border-red-300">
              <TrendingDown className="h-3 w-3 mr-1" />
              Needs Attention
            </Badge>
          </div>
          
          {analytics.negativeFeedback.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-32">
                <div className="text-center">
                  <ThumbsUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-muted-foreground">No negative feedback! 🎉</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {analytics.negativeFeedback.map(renderFeedbackCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default FeedbackManagement